import { useContext, useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Button, PhoneInput } from "@brainfinance/icash-component-library"
import { Loader } from "../../Utilities/Loader"

import { InstantLoanContext } from "../../../Helpers/Reducers/InstantLoan"

import { RequestsContext } from "../../../Helpers/Reducers/Requests"
import { postValidatePhone } from "../../../Helpers/Requests"
import { goToNext } from "../../../Helpers/Routes"
import { toast } from "react-toastify"
import { postMessageSender } from "../../../Helpers/PostMessage"

export function MobileVerification() {
  const [error, setError] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [voipWarning, setVoipWarning] = useState<{ message: string } | null>(null)
  const [unsupportedPhone, setUnsupportedPhone] = useState<{ message: string } | null>(null)

  const navigate = useNavigate()

  const {
    state: { mobileVerification },
    dispatch,
  } = useContext(InstantLoanContext)

  const {
    state: { token },
  } = useContext(RequestsContext)

  const { pathname } = useLocation()
  const path = pathname.substring(1)

  const handleValueChange = (value?: string | number) => {
    let mobileNumber: string | undefined
    if (typeof value !== "string") mobileNumber = value?.toString()
    else mobileNumber = value

    setError(undefined)

    dispatch({ step: "mobileVerification", payload: { mobileNumber } })
  }

  const submitPhone = (voipConfirmed?: boolean) => {
    setIsSubmitting(true)
    postValidatePhone(mobileVerification, token, voipConfirmed)
      .then((result) => {
        if (result.voipDetected) {
          setVoipWarning({ message: result.message })
          return
        }

        postMessageSender({
          event: "marketing",
          payload: {
            status: "track",
            data: {
              event: "app_phone_validation_completed",
              properties: {
                phoneNumber: mobileVerification.mobileNumber,
              },
            },
          },
        })
        goToNext(navigate, path)
      })
      .catch((e) => {
        const message = e.message ?? ""

        postMessageSender({
          event: "marketing",
          payload: {
            status: "track",
            data: {
              event: "app_phone_validation_error",
              properties: {
                error: message,
              },
            },
          },
        })

        if (message.toLowerCase().includes("voip")) {
          setUnsupportedPhone({ message })
          return
        }

        toast(message || e, { type: "error" })
        // scroll to top of document
        document.querySelector("#scroll-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })
      })
      .finally(() => setIsSubmitting(false))
  }

  const handleSubmit = () => {
    if (mobileVerification.mobileNumber && mobileVerification.mobileNumber.length < 12) {
      setError("Enter a valid 10 digit number")
      return
    }

    postMessageSender({
      event: "marketing",
      payload: {
        status: "track",
        data: {
          event: "app_phone_validation_submitted",
          skipCleverTap: true,
          properties: {
            phoneNumber: mobileVerification.mobileNumber,
          },
        },
      },
    })

    submitPhone()
  }

  const handleVoipContinue = () => {
    setVoipWarning(null)
    submitPhone(true)
  }

  const handleVoipClose = () => {
    setVoipWarning(null)
  }

  const handleUnsupportedPhoneClose = () => {
    setUnsupportedPhone(null)
  }

  const handleChangePhoneNumber = () => {
    dispatch({ step: "mobileVerification", payload: { mobileNumber: "" } })
    setError(undefined)
    setVoipWarning(null)
    setUnsupportedPhone(null)
  }

  useEffect(() => {
    postMessageSender({
      event: "marketing",
      payload: {
        status: "screen",
        data: {
          categoryName: "Application",
          pathName: "app_phone_validation_loaded",
        },
      },
    })
  }, [])

  return (
    <Loader loading={!token}>
      <div className="w-[529px] sm:w-full" onKeyDown={(e) => e.key === "Enter" && handleSubmit()}>
        <p className="caption-light !mb-[32px]">
          We will send a text message with a verification code to this phone number.
        </p>
        <PhoneInput
          label="Mobile number"
          value={mobileVerification.mobileNumber}
          hardFillValue={mobileVerification.mobileNumber ?? ""}
          onChange={handleValueChange}
          error={error}
          className="!mb-[32px]"
          disabled={isSubmitting}
        />
        <p className="caption-medium !mb-[32px]">
          *This phone number will be used for two-factor authentication when signing in to your account
        </p>
        <Button
          size="large"
          appearance="primary"
          className="!w-full"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || !!error || !mobileVerification.mobileNumber}
        >
          Send verification code
        </Button>
      </div>

      {voipWarning && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-interface-200/60"
          onClick={handleVoipClose}
        >
          <div
            className="relative mx-4 w-full max-w-[576px] rounded-2xl bg-white p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="title-caption">Phone number alert</h2>
              <button
                onClick={handleVoipClose}
                className="flex h-8 w-8 items-center justify-center rounded-full text-interface-500 hover:bg-interface-100"
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <p className="caption-light !mb-8">{voipWarning.message}</p>
            <div className="flex gap-3">
              <Button size="large" appearance="secondary" className="!flex-1" onClick={handleChangePhoneNumber}>
                Change phone number
              </Button>
              <Button size="large" appearance="primary" className="!flex-1" onClick={handleVoipContinue}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {unsupportedPhone && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-interface-200/60"
          onClick={handleUnsupportedPhoneClose}
        >
          <div
            className="relative mx-4 w-full max-w-[576px] rounded-2xl bg-white p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="title-caption">Unsupported phone number</h2>
              <button
                onClick={handleUnsupportedPhoneClose}
                className="flex h-8 w-8 items-center justify-center rounded-full text-interface-500 hover:bg-interface-100"
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <p className="caption-light !mb-8">{unsupportedPhone.message}</p>
            <Button size="large" appearance="primary" className="!w-full" onClick={handleChangePhoneNumber}>
              Change phone number
            </Button>
          </div>
        </div>
      )}
    </Loader>
  )
}
